import { useContext, useMemo } from 'react';
import classNames from 'classnames';

import { TimelineContext, Timescale } from './Timeline';
import { roundTo } from './TimelineUtils';

import { assert } from '../../utils/assert';

import styles from './TimelineGrid.module.scss';

const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;

const BIG_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'full',
});
const SHORT_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
});

export interface TimelineGridProps {
    timescale: Timescale;
    subTimescale?: Timescale;
}

export const TimelineGrid = ({
    timescale,
    subTimescale,
}: TimelineGridProps) => {
    const context = useContext(TimelineContext);
    assert(context, 'Must be used within Timeline compoennt');

    const {
        lowerBound: lowerBoundRaw,
        upperBound: upperBoundRaw,
        timeToPosition: timeToPixels,
        laneHeight,
    } = context;

    const timezoneOffsetInMs = new Date().getTimezoneOffset() * 60 * 1000;
    const lowerBound = roundTo(lowerBoundRaw, DAY_IN_MS, timezoneOffsetInMs);
    const upperBound = roundTo(upperBoundRaw, DAY_IN_MS, timezoneOffsetInMs);

    const gridLines: Date[] = useMemo(() => {
        const gridLines: Date[] = [];
        const currentDate = new Date(lowerBound);

        const { roundTo, stepSize } = timescale;
        while (currentDate.getTime() < upperBound) {
            const gridLine = roundTo(currentDate);
            gridLines.push(gridLine);
            currentDate.setTime(gridLine.getTime() + stepSize);
        }

        return gridLines;
    }, [upperBound, lowerBound, timescale]);
    const subGridLines: Date[] = useMemo(() => {
        if (!subTimescale) {
            return [];
        }
        const gridLines: Date[] = [];
        const currentDate = new Date(lowerBound);

        const { roundTo, stepSize } = subTimescale;
        while (currentDate.getTime() < upperBound) {
            const gridLine = roundTo(currentDate);
            gridLines.push(gridLine);
            currentDate.setTime(gridLine.getTime() + stepSize);
        }

        return gridLines;
    }, [upperBound, lowerBound, subTimescale]);

    return (
        <>
            {gridLines.map((date) => {
                return (
                    <p
                        key={`grid:${date}`}
                        style={{
                            position: 'absolute',
                            top: laneHeight - 10,
                            transform: 'translateY(-100%) translateX(-50%)',
                            left: timeToPixels(date.getTime()),
                            margin: 0,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {timescale.stepSize >= DAY_IN_MS
                            ? BIG_DATE_FORMAT.format(date)
                            : SHORT_DATE_FORMAT.format(date)}
                    </p>
                );
            })}
            {subGridLines.map((date) => {
                return (
                    <div
                        key={`grid:${date}`}
                        style={{
                            top: laneHeight - 10,
                            transform: `translateX(${Math.round(
                                timeToPixels(date.getTime()),
                            )}px)`,
                        }}
                        className={classNames(styles.gridLine, styles.subgrid)}
                    ></div>
                );
            })}
            {gridLines.map((date) => {
                return (
                    <div
                        key={`grid:${date}`}
                        style={{
                            top: laneHeight - 10,
                            transform: `translateX(${Math.round(
                                timeToPixels(date.getTime()),
                            )}px)`,
                        }}
                        className={styles.gridLine}
                    ></div>
                );
            })}
            <div className={styles.legendContainer}>
                <strong>Legend</strong>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                    }}
                >
                    <div className={styles.legendLine} />
                    <p>{timescale.label}</p>
                </div>
                {subTimescale && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                        }}
                    >
                        <div
                            className={classNames(
                                styles.legendLine,
                                styles.subgrid,
                            )}
                        />
                        <p>{subTimescale.label}</p>
                    </div>
                )}
            </div>
        </>
    );
};
